import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { photoAlbums } from '../data/photoAlbums';
import blogList from '../data/blog/blog-list.json';

const Photos = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Derived state from URL
  const filter = {
    textVal: searchParams.get('search') || '',
    publicOnly: searchParams.get('public') === 'true',
    year: searchParams.get('year') ? Number(searchParams.get('year')) : null,
    tag: searchParams.get('tag') || searchParams.get('label') || null, // Handle both for legacy
    city: searchParams.get('city') || null,
    country: searchParams.get('country') || null,
    continent: searchParams.get('continent') || null
  };
  const selectedPage = searchParams.get('page') ? Math.max(0, Number(searchParams.get('page')) - 1) : 0;

  const [pagedResults, setPagedResults] = useState([]);
  const [pages, setPages] = useState([]);
  const [years, setYears] = useState([]);
  const [tags, setTags] = useState([]);
  const [cities, setCities] = useState([]);
  const [countries, setCountries] = useState([]);
  const [continents, setContinents] = useState([]);
  const [expandedSection, setExpandedSection] = useState(null);

  const getYear = (date) => Number(date.getFullYear());

  // Helper to update params
  const updateParams = (updates) => {
    const newParams = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value === null || value === '' || value === undefined || value === false) {
        newParams.delete(key);
        if (key === 'tag') newParams.delete('label'); // Also clear legacy
      } else {
        newParams.set(key, value);
      }
    });
    setSearchParams(newParams);
  };

  const search = useCallback(() => {
    let filtered = photoAlbums.filter(album => {
      if (filter.textVal && !album.name.toLowerCase().includes(filter.textVal.toLowerCase())) {
        return false;
      }
      if (filter.publicOnly && !album.public) {
        return false;
      }
      if (filter.year && filter.year !== getYear(album.date)) {
        return false;
      }
      if (filter.tag && !(album.labels || []).includes(filter.tag)) {
        return false;
      }
      if (filter.city && !(album.cities || []).includes(filter.city)) {
        return false;
      }
      if (filter.country && (!(album.countries || []).includes(filter.country) && !(album.country === filter.country))) {
        // Checking both countries array and legacy country single field if it exists
        return false;
      }
      if (filter.continent && !(album.continents || []).includes(filter.continent)) {
        return false;
      }
      return true;
    });

    const counts = {
      years: {},
      tags: {},
      cities: {},
      countries: {},
      continents: {}
    };

    const addToCounts = (key, value) => {
      if (!value) return;
      if (Array.isArray(value)) {
        value.forEach(v => {
          counts[key][v] = (counts[key][v] || 0) + 1;
        });
      } else {
        counts[key][value] = (counts[key][value] || 0) + 1;
      }
    };

    filtered.forEach(album => {
      addToCounts('years', getYear(album.date));
      addToCounts('tags', album.labels);
      addToCounts('cities', album.cities);
      addToCounts('countries', album.countries);
      addToCounts('continents', album.continents);
    });

    const formatCounts = (obj) => Object.entries(obj)
      .map(([value, count]) => {
        const numValue = Number(value);
        const isNumeric = !isNaN(numValue) && value.trim() !== "" && /^\d+$/.test(value);
        return { value: isNumeric ? numValue : value, count };
      })
      .sort((a, b) => {
        if (typeof a.value === 'number' && typeof b.value === 'number') {
          return b.value - a.value;
        }
        return String(a.value).localeCompare(String(b.value));
      });

    setYears(formatCounts(counts.years));
    setTags(formatCounts(counts.tags));
    setCities(formatCounts(counts.cities));
    setCountries(formatCounts(counts.countries));
    setContinents(formatCounts(counts.continents));

    const itemsPerPage = 12;
    const totalPages = Math.ceil(filtered.length / itemsPerPage);
    const pageArray = [];
    for (let i = 0; i < totalPages; i++) {
      pageArray.push(i);
    }
    setPages(pageArray);

    const startIndex = selectedPage * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    setPagedResults(filtered.slice(startIndex, endIndex));
  }, [filter.textVal, filter.publicOnly, filter.year, filter.tag, filter.city, filter.country, filter.continent, selectedPage]);

  const toggleFilter = (type, val) => {
    const current = filter[type];
    updateParams({ [type]: current === val ? null : val, page: 1 });
  };

  const setSelectedPageHandler = (page) => {
    updateParams({ page: page + 1 });
  };

  useEffect(() => {
    search();
  }, [search]);

  return (
    <section id="about">
      <div className="container">
        <div className="row">
          <div className="col-lg-12 text-center">
            <h2>Photos</h2>
            <hr className="star-primary" />
          </div>
        </div>

        <div className="row divider">
          <div className="col-xs-12" style={{ marginBottom: '15px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
              <span>Selection of my photo albums:</span>
              <input
                className="form-control"
                value={filter.textVal}
                onChange={(e) => updateParams({ search: e.target.value, page: 1 })}
                style={{ width: '200px', display: 'inline-block' }}
              />
              <div
                className="filter-label"
                onClick={() => updateParams({ search: '', page: 1 })}
                style={{ display: filter.textVal ? 'inline-block' : 'none', cursor: 'pointer' }}
              >
                Clear Search X
              </div>
              <label style={{ fontWeight: 'normal', cursor: 'pointer', margin: 0, display: 'flex', alignItems: 'center', gap: '5px' }}>
                <input
                  type="checkbox"
                  checked={filter.publicOnly}
                  onChange={(e) => updateParams({ public: e.target.checked, page: 1 })}
                />
                <i className="fa fa-unlock"></i> Public Only
              </label>
            </div>
          </div>

          {[
            { id: 'year', label: 'Year', items: years, field: 'year' },
            { id: 'tag', label: 'Tags', items: tags, field: 'tag' },
            { id: 'city', label: 'Cities', items: cities, field: 'city' },
            { id: 'country', label: 'Countries', items: countries, field: 'country' },
            { id: 'continent', label: 'Continents', items: continents, field: 'continent' }
          ].map((section) => (
            <div key={section.id} className="col-xs-12" style={{ marginBottom: '10px' }}>
              <div
                onClick={() => setExpandedSection(expandedSection === section.id ? null : section.id)}
                style={{
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  display: 'flex',
                  alignItems: 'center',
                  padding: '5px 0',
                  borderBottom: '1px solid #eee'
                }}
              >
                <i className={`fa fa-fw ${expandedSection === section.id ? 'fa-chevron-down' : 'fa-chevron-right'}`}></i>
                {section.label}
                {filter[section.field] && (
                  <span className={`label badge-${section.id}`} style={{ marginLeft: '10px' }}>
                    {filter[section.field]}
                  </span>
                )}
              </div>

              {expandedSection === section.id && (
                <div style={{ marginTop: '10px', paddingLeft: '20px' }}>
                  {section.items.length > 0 ? (
                    section.items.map((item, index) => (
                      <div
                        key={index}
                        className={`filter-label ${filter[section.field] === item.value ? 'filter-label-sel' : ''} ${section.field === 'city' ? 'filter-city' :
                          section.field === 'country' ? 'filter-country' :
                            section.field === 'continent' ? 'filter-continent' : ''
                          }`}
                        onClick={() => toggleFilter(section.field, item.value)}
                        style={{ marginBottom: '5px' }}
                      >
                        {item.value}&nbsp;<span style={{ fontStyle: 'italic' }}>[{item.count}]</span>
                        {filter[section.field] === item.value && <span> X</span>}
                      </div>
                    ))
                  ) : (
                    <div style={{ fontStyle: 'italic', color: '#999', fontSize: '13px' }}>No items available</div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="row divider table-row">
          <div className="photos-grid">
            {pagedResults.map((album, index) => (
              <div key={index} className="palb-box">
                <div className="palb-header">
                  <span className="palb-title">
                    <i className={!album.public ? 'fa fa-fw fa-lock' : ''}></i>
                    {album.name}
                  </span>
                  &nbsp;
                  <span className="palb-date">{album.date.toLocaleDateString()}</span>
                </div>

                <div className="palb-labels">
                  {[...(album.labels || [])].sort().map((label, labelIndex) => (
                    <div
                      key={labelIndex}
                      className="palb-label filter-label"
                      onClick={() => toggleFilter('tag', label)}
                    >
                      {label}
                    </div>
                  ))}
                  {[...(album.cities || [])].sort().map((city, index) => (
                    <div
                      key={`city-${index}`}
                      className={`palb-label filter-label filter-city ${filter.city === city ? 'filter-label-sel' : ''}`}
                      onClick={() => toggleFilter('city', city)}
                    >
                      {city}
                    </div>
                  ))}
                  {[...(album.countries || [])].sort().map((country, index) => (
                    <div
                      key={`country-${index}`}
                      className={`palb-label filter-label filter-country ${filter.country === country ? 'filter-label-sel' : ''}`}
                      onClick={() => toggleFilter('country', country)}
                    >
                      {country}
                    </div>
                  ))}
                  {[...(album.continents || [])].sort().map((continent, index) => (
                    <div
                      key={`continent-${index}`}
                      className={`palb-label filter-label filter-continent ${filter.continent === continent ? 'filter-label-sel' : ''}`}
                      onClick={() => toggleFilter('continent', continent)}
                    >
                      {continent}
                    </div>
                  ))}
                </div>

                <div className="palb-media">
                  <a href={album.url} target="_blank" rel="noopener noreferrer">
                    {album.thumb && (
                      <img className="palb-img" src={`/img/photos/${album.thumb}`} alt={album.name} />
                    )}
                  </a>
                </div>

                {(album.countries || []).length > 0 && (
                  <div className="palb-footer" style={{ fontSize: '13px' }}>
                    {blogList.filter(post =>
                      (post.countries || []).some(pc => (album.countries || []).map(ac => ac.toLowerCase()).includes(pc.toLowerCase()))
                    ).slice(0, 1).map(matchingPost => (
                      <Link key={matchingPost.id} to={`/blog/${matchingPost.id}`} style={{ color: '#007bff' }}>
                        <i className="fa fa-file-text-o"></i> Read matching blog: {matchingPost.title}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="col-xs-12" style={{ textAlign: 'center' }}>
          <div
            className="filter-label filter-label-paging-btn"
            onClick={() => setSelectedPageHandler(selectedPage - 1)}
            style={{ display: selectedPage > 0 ? 'inline-block' : 'none' }}
          >
            &lt;
          </div>

          {pages.map((page) => (
            <div
              key={page}
              className={`filter-label filter-label-paging-btn ${selectedPage === page ? 'filter-label-sel' : ''}`}
              onClick={() => setSelectedPageHandler(page)}
            >
              {page + 1}
            </div>
          ))}

          <div
            className="filter-label filter-label-paging-btn"
            onClick={() => setSelectedPageHandler(selectedPage + 1)}
            style={{ display: selectedPage < pages.length - 1 ? 'inline-block' : 'none' }}
          >
            &gt;
          </div>
        </div>
      </div>
    </section>
  );
};

export default Photos;
